import { useState } from "react";
import { useChangeLog, useRevertChange } from "../hooks";
import type { ChangeLogEntry } from "../types";

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("pl-PL");
}

function apiDetail(e: unknown): string | undefined {
  return (
    e as { response?: { data?: { errors?: { detail?: string } } } }
  )?.response?.data?.errors?.detail;
}

/** Kolejne wpisy jednej kaskady (wspólny batch_id) sklejone w grupę. */
function groupEntries(
  entries: ChangeLogEntry[],
): { batchId: string | null; items: ChangeLogEntry[] }[] {
  const groups: { batchId: string | null; items: ChangeLogEntry[] }[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (entry.batch_id && last && last.batchId === entry.batch_id)
      last.items.push(entry);
    else groups.push({ batchId: entry.batch_id, items: [entry] });
  }
  return groups;
}

function EntryRow({
  entry,
  canEdit,
  onRevert,
  reverting,
}: {
  entry: ChangeLogEntry;
  canEdit: boolean;
  onRevert: (logId: string) => void;
  reverting: boolean;
}) {
  const soilChanged = entry.old_soil_category !== entry.new_soil_category;
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1">
      <span className="tabular-nums text-[var(--color-text-muted)]">
        poz. {entry.position_lp}
      </span>
      <span
        className="max-w-72 truncate text-[var(--color-text-secondary)]"
        title={entry.position_description}
      >
        {entry.position_description}
      </span>
      <span className="font-mono">
        {entry.old_basis_raw || "—"} → {entry.new_basis_raw || "—"}
      </span>
      {soilChanged && (
        <span>
          (grunt kat. {entry.old_soil_category || "—"} →{" "}
          {entry.new_soil_category || "—"})
        </span>
      )}
      {entry.reverted_at ? (
        <span
          className="inline-block border border-stone-300 bg-stone-50 px-1 py-px text-[9px] font-medium text-stone-700"
          title={`Wycofano ${when(entry.reverted_at)}${
            entry.reverted_by_name ? ` — ${entry.reverted_by_name}` : ""
          }`}
        >
          WYCOFANA
        </span>
      ) : (
        canEdit && (
          <button
            className="text-[var(--color-primary)] hover:underline disabled:opacity-50"
            disabled={reverting}
            title={`Przywróć podstawę ${entry.old_basis_raw || "—"} wraz z ${
              entry.old_inputs_count
            } nakładami sprzed zmiany`}
            onClick={() => onRevert(entry.id)}
          >
            cofnij
          </button>
        )
      )}
    </li>
  );
}

/** Trwały dziennik zmian podstaw / kategorii gruntu (kto, kiedy, z czego na co)
 *  z wycofaniem pojedynczej zmiany albo całej kaskady. `expansion_warnings`
 *  na pozycji pamięta tylko ostatnią zmianę — ta karta pokazuje wszystkie. */
export function ChangeHistoryCard({
  jobId,
  canEdit,
}: {
  jobId: string;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const { data: entries = [], isLoading } = useChangeLog(jobId, open);
  const revert = useRevertChange(jobId);

  const doRevert = (payload: { log_id?: string; batch_id?: string }) => {
    setNotice("");
    revert.mutate(payload, {
      onSuccess: ({ meta }) => {
        const r = meta.revert;
        const skipped = r.skipped
          .map((s) => `poz. ${s.lp}: ${s.reason}`)
          .join("; ");
        const n = r.reverted.length;
        const word =
          n === 1
            ? "zmianę"
            : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)
              ? "zmiany"
              : "zmian";
        setNotice(
          `Wycofano ${n} ${word}.${skipped ? ` Pominięto — ${skipped}` : ""}`,
        );
      },
      onError: (e: unknown) =>
        setNotice(apiDetail(e) || "Nie udało się wycofać zmiany."),
    });
  };

  return (
    <div className="card p-4">
      <button
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="section-label">
          Historia zmian podstaw i kategorii gruntu
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          {open ? "zwiń ▴" : "pokaż ▾"}
        </span>
      </button>
      {open && (
        <div className="mt-2 text-xs">
          {notice && (
            <p className="mb-2 border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1">
              {notice}
            </p>
          )}
          {isLoading && (
            <p className="text-[var(--color-text-muted)]">Ładowanie…</p>
          )}
          {!isLoading && entries.length === 0 && (
            <p className="text-[var(--color-text-muted)]">
              Brak zmian podstaw ani kategorii gruntu w tej wycenie.
            </p>
          )}
          <ul className="divide-y divide-[var(--color-border)]">
            {groupEntries(entries).map((group) =>
              group.batchId && group.items.length > 1 ? (
                <li key={group.batchId} className="py-1.5">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium">
                      Kaskada: {group.items[0].kind_display.toLowerCase()} →
                      grunt kat. {group.items[0].new_soil_category || "—"} (
                      {group.items.length} pozycji)
                    </span>
                    <span className="text-[var(--color-text-muted)]">
                      {when(group.items[0].changed_at)} —{" "}
                      {group.items[0].changed_by_name}
                    </span>
                    {canEdit && group.items.some((i) => !i.reverted_at) && (
                      <button
                        className="text-[var(--color-primary)] hover:underline disabled:opacity-50"
                        disabled={revert.isPending}
                        title="Wycofaj całą kaskadę — każda pozycja wraca do stanu sprzed zmiany"
                        onClick={() => doRevert({ batch_id: group.batchId! })}
                      >
                        cofnij całość
                      </button>
                    )}
                  </div>
                  <ul className="mt-0.5 pl-4">
                    {group.items.map((entry) => (
                      <EntryRow
                        key={entry.id}
                        entry={entry}
                        canEdit={canEdit}
                        reverting={revert.isPending}
                        onRevert={(id) => doRevert({ log_id: id })}
                      />
                    ))}
                  </ul>
                </li>
              ) : (
                group.items.map((entry) => (
                  <li key={entry.id} className="py-1.5">
                    <div className="text-[var(--color-text-muted)]">
                      {when(entry.changed_at)} — {entry.changed_by_name} —{" "}
                      {entry.kind_display.toLowerCase()}
                    </div>
                    <ul>
                      <EntryRow
                        entry={entry}
                        canEdit={canEdit}
                        reverting={revert.isPending}
                        onRevert={(id) => doRevert({ log_id: id })}
                      />
                    </ul>
                  </li>
                ))
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
